import * as React from 'react';
import { UserTable } from '../components/UserTable';
import { UsersService } from '@/services/users-service';
import { User } from '../types';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/hooks/use-toast';

export function AllUsersPage() {
  const [users, setUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { toast } = useToast();

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await UsersService.getUsers();
      setUsers(response.results);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserStatusToggle = async (userId: string, isActive: boolean) => {
    try {
      const updatedUser = await UsersService.toggleUserStatus(userId, isActive);
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? updatedUser : user
        )
      );
      toast({
        title: 'Success',
        description: `User ${isActive ? 'activated' : 'deactivated'} successfully`,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user status';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleUserDelete = async (userId: string) => {
    try {
      await UsersService.deleteUser(userId);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      toast({
        title: 'Success',
        description: 'User deleted successfully',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handlePasswordReset = async (userId: string) => {
    try {
      await UsersService.resetUserPassword(userId);
      toast({
        title: 'Success',
        description: 'Password reset request sent successfully',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="User Management"
          description="View and manage all users in the system."
        />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="User Management"
          description="View and manage all users in the system."
        />
        <div className="text-center py-12">
          <p className="text-destructive mb-4">Error loading users: {error}</p>
          <button 
            onClick={fetchUsers}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        description="View and manage all users in the system."
      />
      <UserTable 
        data={users} 
        onStatusToggle={handleUserStatusToggle}
        onDelete={handleUserDelete}
        onPasswordReset={handlePasswordReset}
      />
    </div>
  );
}
